import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

function Merchants() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentMerchant, setCurrentMerchant] = useState({ name: '', merchant_id: '' });

  const fetchMerchants = async () => {
    try {
      const response = await axios.get('/admin/merchants');
      setMerchants(response.data.merchants);
      setLoading(false);
    } catch (err) {
      setError('Failed to load merchants');
      setLoading(false);
      console.error('Merchants fetch error:', err);
    }
  };

  useEffect(() => {
    fetchMerchants();
  }, []);

  const handleOpenDialog = (merchant = null) => {
    if (merchant) {
      setEditMode(true);
      setCurrentMerchant(merchant);
    } else {
      setEditMode(false);
      setCurrentMerchant({ name: '', merchant_id: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentMerchant(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await axios.put(`/admin/merchants/${currentMerchant.id}`, currentMerchant);
      } else {
        await axios.post('/admin/merchants', currentMerchant);
      }
      fetchMerchants();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving merchant:', err);
      alert('Failed to save merchant. Please check console for details.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this merchant?')) {
      try {
        await axios.delete(`/admin/merchants/${id}`);
        fetchMerchants();
      } catch (err) {
        console.error('Error deleting merchant:', err);
        alert('Failed to delete merchant.');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Merchants</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Add New Merchant
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="merchants table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Merchant ID</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {merchants.map((merchant) => (
              <TableRow key={merchant.id}>
                <TableCell>{merchant.id}</TableCell>
                <TableCell>{merchant.name}</TableCell>
                <TableCell>{merchant.merchant_id}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpenDialog(merchant)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => handleDelete(merchant.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editMode ? 'Edit Merchant' : 'Add New Merchant'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="name"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentMerchant.name}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            name="merchant_id"
            label="Merchant ID"
            type="text"
            fullWidth
            variant="outlined"
            value={currentMerchant.merchant_id}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Merchants;